import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader2,
  FileStack,
  UploadCloud,
  Shield,
  Laptop,
  TrendingUp,
  Heart,
  ShoppingBag,
  Globe,
} from 'lucide-react'
import { useSession } from '@/lib/session/SessionContext'
import { Logo } from '@/components/brand/Logo'
import { Button } from '@/components/ui/Button'
import { INDUSTRY_TO_DEMO_ID } from '@/lib/demo/constants'

type PathChoice = 'demo' | 'upload'

interface IndustryOption {
  slug: string
  label: string
  Icon: React.ElementType
}

const INDUSTRIES: IndustryOption[] = [
  { slug: 'saas-b2b', label: 'SaaS / B2B', Icon: Laptop },
  { slug: 'insurance', label: 'Insurance', Icon: Shield },
  { slug: 'financial-services', label: 'Financial Services', Icon: TrendingUp },
  { slug: 'healthcare', label: 'Healthcare', Icon: Heart },
  { slug: 'consumer-brands', label: 'Consumer Brands', Icon: ShoppingBag },
  { slug: 'other', label: 'Other', Icon: Globe },
]

const INDUSTRY_CHIPS = [
  { label: 'Insurance', demoId: 'insurance-renewal' },
  { label: 'Financial Services', demoId: 'financial-services-fees' },
  { label: 'Healthcare', demoId: 'healthcare-reminder' },
  { label: 'SaaS', demoId: 'saas-churn' },
]

function guessWorkspaceFromEmail(email: string | undefined): string {
  if (!email) return ''
  const domain = email.split('@')[1]
  if (!domain) return ''
  const base = domain.split('.')[0]
  return base.charAt(0).toUpperCase() + base.slice(1)
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="mb-8">
      <p className="text-sm text-slate-400 mb-2">
        Step {current} / {total}
      </p>
      <div className="flex gap-1.5">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i + 1 <= current ? 'bg-echo-mark' : 'bg-border-gray'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const { session } = useSession()

  const [step, setStep] = useState(1)
  const [workspaceName, setWorkspaceName] = useState('')
  const [industrySlag, setIndustrySlug] = useState('')
  const [choice, setChoice] = useState<PathChoice | null>(null)
  const [targetDemoId, setTargetDemoId] = useState<string>('saas-churn')

  // Prefill workspace name from stored email
  useEffect(() => {
    const stored = localStorage.getItem('echo_user_email') ?? (session as any)?.email
    const guess = guessWorkspaceFromEmail(stored)
    if (guess) setWorkspaceName(guess)
  }, [session])

  // Step 3 — API call
  useEffect(() => {
    if (step !== 3) return
    let cancelled = false

    const run = async () => {
      const sessionToken = localStorage.getItem('echo_session')
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`

      const body = JSON.stringify({
        workspace_name: workspaceName,
        industry_slug: industrySlag,
      })

      let projectId: string | undefined

      try {
        // Primary endpoint
        const res = await fetch('/api/onboarding', { method: 'POST', headers, body })
        if (res.ok) {
          const data = await res.json() as { project_id?: string }
          projectId = data.project_id
        } else {
          throw new Error('onboarding endpoint failed')
        }
      } catch {
        // Fallback endpoint
        try {
          const res2 = await fetch('/api/projects', {
            method: 'POST',
            headers,
            body: JSON.stringify({ name: workspaceName, industry: industrySlag }),
          })
          if (res2.ok) {
            const data2 = await res2.json() as { project_id?: string }
            projectId = data2.project_id
          }
        } catch {
          // ignore — fall through to demo
        }
      }

      if (cancelled) return

      if (choice === 'demo' || !projectId) {
        navigate('/app/demo?industry=' + targetDemoId, { replace: true })
      } else {
        navigate(`/app/projects/${projectId}/upload`, { replace: true })
      }
    }

    run().catch(() => {
      if (!cancelled) navigate('/app/demo?industry=' + targetDemoId, { replace: true })
    })

    return () => { cancelled = true }
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  const canContinueStep1 = workspaceName.trim().length > 0 && industrySlag.length > 0

  // Derive demo description from selected industry
  const selectedIndustry = INDUSTRIES.find(i => i.slug === industrySlag)
  const demoDescription = selectedIndustry
    ? `Run a realistic ${selectedIndustry.label} analysis on demo data.`
    : 'Run a realistic SaaS churn analysis on demo data.'

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <Logo size="sm" />
        <button
          className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
          onClick={() => navigate('/app/demo', { replace: true })}
        >
          Skip setup
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-lg">

          {/* ── STEP 1: Workspace setup ── */}
          {step === 1 && (
            <div className="space-y-6">
              <StepIndicator current={1} total={3} />

              <div>
                <h1 className="text-2xl font-medium text-deep-ink">Set up your workspace</h1>
                <p className="text-slate-500 text-sm mt-1">Usually your agency or team name.</p>
              </div>

              {/* Workspace name */}
              <div>
                <label
                  className="block text-sm font-medium text-deep-ink mb-1.5"
                  htmlFor="workspace-name"
                >
                  Workspace name
                </label>
                <input
                  id="workspace-name"
                  type="text"
                  value={workspaceName}
                  onChange={e => setWorkspaceName(e.target.value)}
                  placeholder="e.g. Acme Research"
                  autoFocus
                  className="w-full px-4 py-2.5 border border-border-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-echo-mark/30 focus:border-echo-mark bg-white placeholder:text-slate-400"
                />
              </div>

              {/* Industry grid */}
              <div>
                <p className="text-sm font-medium text-deep-ink mb-2">Industry</p>
                <div className="grid grid-cols-2 gap-3">
                  {INDUSTRIES.map(({ slug, label, Icon }) => (
                    <div
                      key={slug}
                      role="button"
                      tabIndex={0}
                      data-testid="industry-card"
                      data-industry={slug}
                      onClick={() => setIndustrySlug(slug)}
                      onKeyDown={e => e.key === 'Enter' && setIndustrySlug(slug)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors select-none ${
                        industrySlag === slug
                          ? 'border-echo-indigo bg-indigo-50 text-echo-indigo'
                          : 'border-border-gray text-slate-600 hover:border-echo-mark/50 hover:bg-soft-panel'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                variant="primary"
                size="lg"
                className="w-full"
                disabled={!canContinueStep1}
                onClick={() => setStep(2)}
              >
                Continue →
              </Button>
            </div>
          )}

          {/* ── STEP 2: Path choice ── */}
          {step === 2 && (
            <div className="space-y-6">
              <StepIndicator current={2} total={3} />

              <div>
                <h1 className="text-2xl font-medium text-deep-ink">How would you like to start?</h1>
                <p className="text-slate-500 text-sm mt-1">
                  Both options are free. You can switch any time.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Demo path */}
                <div
                  data-testid="path-card-demo"
                  className="flex flex-col gap-4 p-5 border border-border-gray rounded-xl bg-white hover:border-echo-mark/50 hover:shadow-sm transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-echo-mark/8 flex items-center justify-center">
                    <FileStack className="w-5 h-5 text-echo-mark" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-deep-ink">See it in 60 seconds</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      {demoDescription}
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      const demoId = INDUSTRY_TO_DEMO_ID[industrySlag] ?? 'saas-churn'
                      setTargetDemoId(demoId)
                      setChoice('demo')
                      setStep(3)
                    }}
                  >
                    See the demo result
                  </Button>
                </div>

                {/* Upload path */}
                <div
                  data-testid="path-card-upload"
                  className="flex flex-col gap-4 p-5 border border-border-gray rounded-xl bg-white hover:border-echo-mark/50 hover:shadow-sm transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-echo-mark/8 flex items-center justify-center">
                    <UploadCloud className="w-5 h-5 text-echo-mark" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-deep-ink">Use my own data</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Drop your CSV. We will clean it and auto-generate your first pre-read.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => { setChoice('upload'); setStep(3) }}
                  >
                    Upload my data
                  </Button>
                </div>
              </div>

              {/* Industry chips */}
              <div className="flex flex-wrap gap-2">
                {INDUSTRY_CHIPS.map(({ label, demoId }) => (
                  <button
                    key={demoId}
                    data-testid="industry-demo-chip"
                    data-industry={demoId}
                    className="px-3 py-1 text-xs font-medium rounded-full border border-border-gray text-slate-600 hover:border-echo-mark/60 hover:text-echo-mark hover:bg-soft-panel transition-colors"
                    onClick={() => navigate('/app/demo?industry=' + demoId)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <button
                className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
                onClick={() => setStep(1)}
              >
                ← Back
              </button>
            </div>
          )}

          {/* ── STEP 3: Creating ── */}
          {step === 3 && (
            <div className="flex flex-col items-center gap-4 py-16">
              <StepIndicator current={3} total={3} />
              <Loader2 className="w-10 h-10 text-echo-mark animate-spin" />
              <p className="text-base font-medium text-deep-ink">Setting up your workspace...</p>
              <p className="text-sm text-slate-400">This only takes a moment.</p>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
